import { useRef, useState, useEffect } from "react";
import api from "../lib/api";
import sanitizeHtml from "../lib/sanitizeHtml";
import { Bold, Italic, Link as LinkIcon, Quote, Code, Smile, Image as ImageIcon, ListOrdered, List, Eraser, Undo, Redo, Paperclip, ChevronDown, X, Video, FileText, Type, Palette, MoreVertical, AlignLeft, Grid, Brackets, Save, FileSearch, Reply, AlignCenter, AlignRight } from "lucide-react";
import { toast } from "sonner";

const EMOJI_CATEGORIES = {
  "Gaming": ["🎮", "👾", "🕹️", "🎲", "🎯", "🏆", "🥇", "🥈", "🥉", "🏅", "🎧", "📺"],
  "Faces": ["😀", "😂", "🥰", "😎", "🤔", "😴", "🤯", "😭", "😡", "🤢", "🤡", "👻"],
  "Hands": ["👍", "👎", "👏", "🙌", "🤝", "✌️", "🤞", "🤟", "🤘", "👌", "🤌", "✍️"],
  "Hearts": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❤️‍🔥", "❤️‍🩹"],
  "Animals": ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯", "🦁", "🐮"],
};

const BASIC_COLORS = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffffff', '#aaaaaa'];

export default function ReplyEditor({ value, onChange, onSubmit, attachments = [], onAttachmentsChange, placeholder = "Scrie un răspuns..." }) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [showSize, setShowSize] = useState(false);
  const [showAlign, setShowAlign] = useState(false);
  const [emojiCat, setEmojiCat] = useState("Gaming");
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkModal, setLinkModal] = useState({ open: false, url: "", text: "" });
  
  const [active, setActive] = useState({});

  const editorRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const draft = localStorage.getItem("reply_draft");
    if (draft && !value && editorRef.current) {
      editorRef.current.innerHTML = draft;
      onChange(draft);
    }
  }, []);

  const checkActive = () => {
    setActive({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      list: document.queryCommandState("insertUnorderedList"),
      olist: document.queryCommandState("insertOrderedList"),
    });
  };

  const handleInput = (e) => {
    onChange(e.currentTarget.innerHTML);
    checkActive();
  };

  const exec = (cmd, val = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(cmd, false, val);
    checkActive();
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertHtml = (html) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand('insertHTML', false, html);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };
  
  const applySpecialEffect = (className) => {
    const sel = window.getSelection();
    let text = sel.toString();
    if (!text) text = "Text";
    insertHtml(`<span class="${className}">${text}</span> `);
    setShowColor(false);
  };

  const handleSave = () => {
    localStorage.setItem("reply_draft", value || "");
    toast.success("Ciornă salvată");
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} prea mare (max 10MB)`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const kind = f.type.startsWith("image/") ? "image" : f.type.startsWith("video/") ? "video" : "file";
        const { data } = await api.post(`/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        uploaded.push({ id: data.id, url: fullUrl, name: f.name, type: f.type, size: f.size, kind });
      }
      onAttachmentsChange?.([...attachments, ...uploaded]);
      toast.success(`${uploaded.length} fișier(e) atașate`);
    } catch (err) {
      toast.error("Încărcare eșuată");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    onAttachmentsChange?.(attachments.filter((a) => a.id !== id));
  };

  const Btn = ({ icon: Icon, action, title, isActive, customClass = "", onClick }) => {
    const handleClick = (e) => {
      e.preventDefault();
      if (onClick) onClick();
      else exec(action);
    };
    return (
      <button 
        type="button" 
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleClick}
        title={title}
        className={`w-7 h-7 flex items-center justify-center border transition-all ${
          isActive 
            ? 'bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border-[var(--tf-primary)]/50 font-bold' 
            : 'bg-[#090c12] hover:bg-[#0e131d] border-[#141824] hover:border-[#181e2b] text-zinc-400 hover:text-white'
        } ${customClass}`}
      >
        <Icon className="w-3.5 h-3.5" />
      </button>
    );
  };

  return (
    <div className="w-full flex flex-col h-full bg-[#06080d] border border-[#141824] overflow-hidden shadow-2xl" style={{ minHeight: '300px' }} data-testid="reply-editor">
      <input ref={fileRef} type="file" multiple accept="image/*,video/*,.pdf,.txt,.zip" onChange={handleFile} hidden />
      
      {/* Top Toolbar */}
      <div className="flex items-center gap-1.5 p-2 border-b border-[#141824] bg-[#06080d] flex-wrap relative">
        
        {/* Group 1 */}
        <div className="flex items-center gap-1 relative">
          <Btn icon={Eraser} action="removeFormat" title="Curăță formatarea" />
          <Btn icon={Bold} action="bold" title="Bold" isActive={active.bold} />
          <Btn icon={Italic} action="italic" title="Italic" isActive={active.italic} />
          
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => {setShowSize(!showSize); setShowColor(false); setShowAlign(false);}} 
            className="h-7 px-2 flex items-center justify-center bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] text-zinc-400 hover:text-white transition-colors gap-1 text-xs" 
            title="Mărime font"
          >
            <Type className="w-3.5 h-3.5" /> 
            <ChevronDown className="w-3 h-3"/>
          </button>

          {showSize && (
            <div className="absolute top-full left-12 mt-1 w-24 bg-[#090c12] border border-[#181e2b] shadow-2xl z-50 py-1 overflow-hidden">
              {[{sz: 1, px: 10}, {sz: 2, px: 13}, {sz: 3, px: 16}, {sz: 4, px: 18}, {sz: 5, px: 24}, {sz: 6, px: 32}].map(f => (
                <button key={f.sz} onMouseDown={(e) => e.preventDefault()} onClick={() => {exec('fontSize', f.sz); setShowSize(false);}} className="w-full text-left px-3 py-1.5 hover:bg-[#0e131d] text-xs font-mono text-zinc-300 hover:text-white">
                  {f.px}px
                </button>
              ))}
            </div>
          )}

          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => {setShowColor(!showColor); setShowSize(false); setShowAlign(false);}} 
            className="w-7 h-7 flex items-center justify-center bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] text-zinc-400 hover:text-white transition-colors" 
            title="Culoare text"
          >
            <Palette className="w-3.5 h-3.5" />
          </button>

          {showColor && (
            <div className="absolute top-full left-20 mt-1 bg-[#090c12] border border-[#181e2b] shadow-2xl z-50 p-3 w-64 flex flex-col gap-3">
              <div className="grid grid-cols-4 gap-2">
                {BASIC_COLORS.map(c => (
                  <button key={c} onMouseDown={(e) => e.preventDefault()} onClick={() => {exec('foreColor', c); setShowColor(false);}} className="w-8 h-8 hover:scale-110 transition-transform mx-auto border border-black" style={{backgroundColor: c}} />
                ))}
              </div>
              <div className="h-px w-full bg-[#141824]" />
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-zinc-500 font-bold uppercase font-chakra tracking-widest px-1 mb-1">Efecte Speciale</span>
                <button onMouseDown={(e) => e.preventDefault()} onClick={() => applySpecialEffect('text-anim-rainbow')} className="w-full text-left px-2 py-1.5 hover:bg-[#0e131d] text-xs font-bold text-anim-rainbow transition-colors">Rainbow Animat</button>
                <button onMouseDown={(e) => e.preventDefault()} onClick={() => applySpecialEffect('text-grad-gold')} className="w-full text-left px-2 py-1.5 hover:bg-[#0e131d] text-xs font-bold text-grad-gold transition-colors">Gold Gradient</button>
                <button onMouseDown={(e) => e.preventDefault()} onClick={() => applySpecialEffect('text-grad-silver')} className="w-full text-left px-2 py-1.5 hover:bg-[#0e131d] text-xs font-bold text-grad-silver transition-colors">Silver Gradient</button>
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-[#141824] mx-1" />

        {/* Group 2 */}
        <div className="flex items-center gap-1 relative">
          <Btn icon={ListOrdered} action="insertOrderedList" title="Listă ordonată" isActive={active.olist} />
          <Btn icon={List} action="insertUnorderedList" title="Listă neordonată" isActive={active.list} />
          <button 
            type="button" 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={() => {setShowAlign(!showAlign); setShowColor(false); setShowSize(false);}} 
            className="h-7 px-2 flex items-center justify-center bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] text-zinc-400 hover:text-white transition-colors gap-1 text-xs"
          >
            <AlignLeft className="w-3.5 h-3.5" /> 
            <ChevronDown className="w-3 h-3"/>
          </button>

          {showAlign && (
            <div className="absolute top-full right-0 mt-1 w-28 bg-[#090c12] border border-[#181e2b] shadow-2xl z-50 py-1 flex flex-col overflow-hidden">
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => {exec('justifyLeft'); setShowAlign(false);}} className="w-full text-left px-3 py-1.5 hover:bg-[#0e131d] text-xs text-zinc-300 hover:text-white flex items-center gap-2"><AlignLeft className="w-3.5 h-3.5"/> Stânga</button>
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => {exec('justifyCenter'); setShowAlign(false);}} className="w-full text-left px-3 py-1.5 hover:bg-[#0e131d] text-xs text-zinc-300 hover:text-white flex items-center gap-2"><AlignCenter className="w-3.5 h-3.5"/> Centru</button>
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => {exec('justifyRight'); setShowAlign(false);}} className="w-full text-left px-3 py-1.5 hover:bg-[#0e131d] text-xs text-zinc-300 hover:text-white flex items-center gap-2"><AlignRight className="w-3.5 h-3.5"/> Dreapta</button>
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-[#141824] mx-1" />

        {/* Group 3 */}
        <div className="flex items-center gap-1">
          <Btn icon={LinkIcon} onClick={() => setLinkModal({ open: true, url: "", text: "" })} title="Link" />
          <Btn icon={ImageIcon} onClick={() => fileRef.current?.click()} title="Imagine" />
          <Btn icon={Smile} onClick={() => setShowEmoji(!showEmoji)} title="Emoji" />
          <Btn icon={Quote} onClick={() => insertHtml('<blockquote>Quote text here...</blockquote>')} title="Citat" />
          <Btn icon={Grid} onClick={() => insertHtml('<table border="1" cellpadding="5" style="border-collapse:collapse; width:100%; border: 1px solid #141824"><tr><th>Header 1</th><th>Header 2</th></tr><tr><td>Data 1</td><td>Data 2</td></tr></table>')} title="Tabel" />
        </div>

        <div className="flex-1" />

        {/* Group 4 (Right tools) */}
        <div className="flex items-center gap-1">
          <Btn icon={Undo} action="undo" title="Undo" />
          <Btn icon={Redo} action="redo" title="Redo" />
          <Btn icon={Brackets} onClick={() => insertHtml('<pre><code>code here...</code></pre>')} title="Cod" />
          <button 
            type="button" 
            onClick={handleSave} 
            className="h-7 px-2.5 flex items-center justify-center bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] text-zinc-400 hover:text-white transition-colors gap-1 text-xs"
            title="Salvează ciornă"
          >
            <Save className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Second Row (Preview Toggle) */}
      <div className="flex items-center px-3 py-1.5 bg-[#06080d] border-b border-[#141824]">
        <button 
          type="button" 
          onClick={() => setPreview(!preview)} 
          className={`h-6 px-2.5 flex items-center justify-center text-xs font-chakra font-bold uppercase tracking-wider transition-colors gap-1.5 ${
            preview 
              ? 'bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border border-[var(--tf-primary)]/40' 
              : 'hover:bg-[#0e131d] text-zinc-400 hover:text-white border border-transparent'
          }`}
        >
          <FileSearch className="w-3.5 h-3.5" /> 
          <span>Preview</span>
        </button>
      </div>

      {/* Emoji Panel */}
      {showEmoji && (
        <div className="border-b border-[#141824] bg-[#06080d]">
          <div className="flex gap-1 px-3 py-1.5 border-b border-[#141824] overflow-x-auto">
            {Object.keys(EMOJI_CATEGORIES).map((c) => (
              <button 
                key={c} 
                type="button" 
                onClick={() => setEmojiCat(c)} 
                className={`text-[10px] font-chakra font-bold uppercase tracking-wider px-2 py-1 ${
                  emojiCat === c ? "bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border border-[var(--tf-primary)]/40" : "text-zinc-400 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="px-3 py-2 grid grid-cols-12 gap-1 max-h-32 overflow-y-auto">
            {EMOJI_CATEGORIES[emojiCat].map((e) => (
              <button key={e} type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => { insertHtml(e); setShowEmoji(false); }} className="text-xl hover:bg-white/10 w-8 h-8 flex items-center justify-center">
                {e}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* WYSIWYG Editor Area */}
      <style dangerouslySetInnerHTML={{__html: `
        .ce-editor:empty:before { content: attr(data-placeholder); color: #52525b; pointer-events: none; }
        .ce-editor blockquote { border-left: 2px solid var(--tf-primary); padding-left: 12px; margin-top: 8px; margin-bottom: 8px; color: #a1a1aa; background: rgba(0,0,0,0.3); padding: 8px 12px; }
        .ce-editor table { border-collapse: collapse; margin: 10px 0; width: 100%; border: 1px solid #141824; }
        .ce-editor th, .ce-editor td { border: 1px solid #141824; padding: 8px; }
        .ce-editor a { color: var(--tf-primary); text-decoration: underline; }
        
        .text-grad-gold {
          background: linear-gradient(to right, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c);
          -webkit-background-clip: text;
          color: transparent;
        }
        .text-grad-silver {
          background: linear-gradient(to right, #c0c0c0, #ececec, #a6a6a6, #f2f2f2, #8c8c8c);
          -webkit-background-clip: text;
          color: transparent;
        }
        .text-anim-rainbow {
          background: linear-gradient(270deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3);
          background-size: 400% 400%;
          -webkit-background-clip: text;
          color: transparent;
          animation: rainbowText 5s ease infinite;
        }
        @keyframes rainbowText {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}} />
      
      {preview ? (
        <div className="flex-1 w-full bg-[#06080d] border-none outline-none px-5 py-4 text-zinc-100 overflow-y-auto min-h-[160px]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyUp={checkActive}
          onMouseUp={checkActive}
          data-placeholder={placeholder}
          className="ce-editor flex-1 w-full bg-[#06080d] border-none outline-none px-5 py-4 text-zinc-100 overflow-y-auto min-h-[160px] text-sm leading-relaxed font-sans"
        />
      )}

      {/* Attachment Previews */}
      {attachments.length > 0 && (
        <div className="px-4 py-3 bg-[#06080d] border-t border-[#141824] flex flex-wrap gap-2">
          {attachments.map((a) => (
            <div key={a.id} className="relative group bg-[#090c12] p-1.5 pr-7 flex items-center gap-2 text-xs border border-[#141824]">
              {a.kind === "image" ? <ImageIcon className="w-4 h-4 text-cyan-400" /> : a.kind === "video" ? <Video className="w-4 h-4 text-purple-400" /> : <FileText className="w-4 h-4 text-zinc-400" />}
              <span className="text-zinc-300 max-w-[120px] truncate font-mono text-[11px]">{a.name}</span>
              <button type="button" onClick={() => removeAttachment(a.id)} className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-black hover:bg-rose-600 text-white">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Footer Bar */}
      <div className="bg-[#06080d] border-t border-[#141824] p-3 flex flex-wrap items-center justify-between gap-4 mt-auto">
        <button 
          type="button" 
          onClick={() => fileRef.current?.click()} 
          className="text-xs text-zinc-300 hover:text-white font-chakra font-bold uppercase tracking-wider bg-[#090c12] hover:bg-[#0e131d] border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-colors flex items-center gap-2 px-3.5 py-2 cursor-pointer"
        >
          <Paperclip className="w-3.5 h-3.5 text-[var(--tf-primary)]" /> 
          <span>ATTACH FILES</span>
        </button>
        
        {onSubmit && (
          <button 
            type="button" 
            onClick={() => { onSubmit(); if(editorRef.current) editorRef.current.innerHTML = ""; localStorage.removeItem("reply_draft"); }} 
            disabled={!value?.trim()} 
            className="bg-[var(--tf-primary)] hover:brightness-110 text-black text-xs font-chakra font-black uppercase tracking-wider px-6 py-2.5 transition-all flex items-center gap-2 shadow-[0_0_15px_rgba(255,79,0,0.3)] disabled:opacity-50 cursor-pointer"
          >
            <Reply className="w-4 h-4" /> 
            <span>POST REPLY</span>
          </button>
        )}
      </div>

      {/* Custom Link Modal */}
      {linkModal.open && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="p-6 w-full max-w-sm border border-[#181e2b] bg-[#090c12] shadow-2xl">
            <h2 className="text-sm font-chakra font-black uppercase text-white mb-4">Adaugă un Link</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-chakra font-bold text-zinc-500 uppercase tracking-widest mb-1">URL</label>
                <input 
                  type="text" 
                  value={linkModal.url} 
                  onChange={(e) => setLinkModal(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full bg-[#06080d] border border-[#141824] px-3 py-2 text-xs text-white outline-none focus:border-[var(--tf-primary)] transition-colors font-mono"
                  placeholder="https://"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[10px] font-chakra font-bold text-zinc-500 uppercase tracking-widest mb-1">Text afișat (opțional)</label>
                <input 
                  type="text" 
                  value={linkModal.text} 
                  onChange={(e) => setLinkModal(prev => ({ ...prev, text: e.target.value }))}
                  className="w-full bg-[#06080d] border border-[#141824] px-3 py-2 text-xs text-white outline-none focus:border-[var(--tf-primary)] transition-colors"
                  placeholder="Ex: Apasă aici"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setLinkModal({ open: false, url: "", text: "" })} className="px-4 py-2 text-xs font-chakra uppercase font-bold text-zinc-400 hover:text-white transition-colors">
                Anulează
              </button>
              <button 
                type="button" 
                onClick={() => {
                  if (linkModal.url) {
                    if (linkModal.text) {
                      insertHtml(`<a href="${linkModal.url}">${linkModal.text}</a>`);
                    } else {
                      insertHtml(`<a href="${linkModal.url}">${linkModal.url}</a>`);
                    }
                  }
                  setLinkModal({ open: false, url: "", text: "" });
                }} 
                className="bg-[var(--tf-primary)] text-black px-4 py-2 text-xs font-chakra uppercase font-black"
              >
                Adaugă
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AttachmentList({ items = [] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-[#141824] flex flex-wrap gap-2">
      {items.map((it, idx) => (
        <a 
          key={idx} 
          href={it.url} 
          target="_blank" 
          rel="noreferrer"
          className="flex items-center gap-2 bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 px-3 py-2 text-xs text-zinc-300 hover:text-white transition-all font-mono"
        >
          <Paperclip className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
          <span className="truncate max-w-[200px]">{it.name || "Attachment"}</span>
        </a>
      ))}
    </div>
  );
}

export function renderMarkdown(txt) {
  if (!txt) return "";
  return txt;
}
